import { Page } from "@betomorrow/micro-stores"
import { useServicesContext } from "@modules/core/services/services.context"
import { MemberDetailedEntity, MemberItemEntity } from "@modules/members/entities/memberEntity"
import { MemberDetailedDto, MemberFilter, MemberItemDto } from "@modules/members/gateway/memberDto"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export const useMembers = (filter: MemberFilter, page?: number, size?: number, nameFilter?: string) => {
  const { memberApi } = useServicesContext()

  const { data, isLoading } = useQuery({
    queryKey: ["boxMembers", filter, page, size, nameFilter],
    queryFn: async () => {
      return await memberApi.getMembers(filter, page, size, nameFilter)
    },
  })

  return {
    data: {
      ...data,
      content: data?.content.map((boxMember) => MemberItemEntity.fromItemDto(boxMember)) || [],
    },
    isLoading,
  }
}

export const useMember = (id: string) => {
  const { memberApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ["boxMember", id],
    queryFn: async () => {
      return await memberApi.getMember(id)
    },
  })

  // Update member
  const { mutateAsync: updateMember, isPending: isUpdateMember } = useMutation({
    mutationFn: async (member: MemberDetailedEntity) => {
      return await memberApi.updateMember(id, member)
    },
    onSuccess: (data: MemberDetailedDto) => {
      queryClient.invalidateQueries({
        queryKey: ["boxMember", id],
      })

      // Update the single member in the list
      // Currently only the note can be edited on the member
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === "boxMembers" },
        (oldData: Page<MemberItemDto> | undefined) => ({
          ...oldData,
          content: oldData?.content.map((member) => {
            if (member.id !== id) return member
            return {
              ...member,
              note: data.note,
            }
          }),
        }),
      )
    },
  })

  // Archive member
  const { mutateAsync: archiveMember, isPending: isArchiveMember } = useMutation({
    mutationFn: async () => {
      return await memberApi.archiveMember(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxMembers"],
      })
      queryClient.invalidateQueries({
        queryKey: ["boxMember", id],
      })
    },
  })

  const isPending = isUpdateMember || isArchiveMember

  return {
    data: data ? MemberDetailedEntity.fromDto(data) : undefined,
    error,
    updateMember,
    archiveMember,
    isPending,
    isLoading,
  }
}
